import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useVault } from '../contexts/VaultContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Heart, Plus, Trash2, ShieldCheck, Volume2, ClipboardList, CheckCircle, Edit2, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import EncryptionBadge from '../components/EncryptionBadge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MedicalHistoryPage = () => {
  const navigate = useNavigate();
  const { t, language, speak } = useLanguage();
  const { api, isUnlocked } = useVault();
  const [entries, setEntries] = useState([]);
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    condition: '',
    diagnosed_date: '',
    description: '',
    doctor_name: '',
    status: 'ongoing'
  });

  useEffect(() => {
    if (isUnlocked) {
      fetchData();
    }
  }, [isUnlocked]);

  const fetchData = async () => {
    try {
      // Fetch entries using encrypted API
      const data = await api.getMedicalHistory();
      setEntries(data || []);
      
      // Fetch intake separately to prevent errors from blocking the page
      try {
        const intakeRes = await axios.get(`${API_URL}/health/medical-history/intake`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('sehatSetu_token')}` }
        });
        setIntake(intakeRes.data?.id ? intakeRes.data : null);
      } catch (intakeError) {
        console.log('Intake not found or error:', intakeError);
        setIntake(null);
      }
    } catch (error) {
      console.error('Failed to load medical history:', error);
      toast.error('Failed to load medical history');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await api.updateMedicalHistory(editingEntry.id, formData);
        toast.success(language === 'hi' ? 'अपडेट हो गया!' : 'Record updated!');
      } else {
        await api.createMedicalHistory(formData);
        toast.success(t('recordAdded'));
      }
      setDialogOpen(false);
      setEditingEntry(null);
      setFormData({ condition: '', diagnosed_date: '', description: '', doctor_name: '', status: 'ongoing' });
      fetchData();
    } catch (error) {
      toast.error(editingEntry ? 'Failed to update record' : 'Failed to add record');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      condition: entry.condition || '',
      diagnosed_date: entry.diagnosed_date || '',
      description: entry.description || '',
      doctor_name: entry.doctor_name || '',
      status: entry.status || 'ongoing'
    });
    setDialogOpen(true);
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleDelete = async (id) => {
    setDeleteConfirmId(id);
  };
  
  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await api.deleteMedicalHistory(deleteConfirmId);
      toast.success(language === 'hi' ? 'हटा दिया गया' : 'Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-red-100">
              <Heart className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">{t('medicalHistory')}</h1>
              <div className="flex items-center gap-2">
                <p className="text-slate-500">{entries.length} {t('condition')}s</p>
                <EncryptionBadge variant="compact" />
              </div>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { 
            setDialogOpen(open); 
            if (!open) { 
              setEditingEntry(null);
              setFormData({ condition: '', diagnosed_date: '', description: '', doctor_name: '', status: 'ongoing' });
            } 
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0D2340]" data-testid="add-medical-history-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('addCondition')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingEntry 
                    ? (language === 'hi' ? 'बीमारी संपादित करें' : 'Edit Condition')
                    : t('addCondition')
                  }
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('condition')}</Label>
                  <Input
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    placeholder="e.g., Diabetes, Hypertension"
                    required
                    data-testid="condition-input"
                  />
                </div>
                <div>
                  <Label>{t('diagnosedDate')}</Label>
                  <Input
                    type="date"
                    value={formData.diagnosed_date}
                    onChange={(e) => setFormData({ ...formData, diagnosed_date: e.target.value })}
                    required
                    data-testid="diagnosed-date-input"
                  />
                </div>
                <div>
                  <Label>{t('description')}</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the condition..."
                    data-testid="description-input"
                  />
                </div>
                <div>
                  <Label>{t('doctorName')}</Label>
                  <Input
                    value={formData.doctor_name}
                    onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                    placeholder="Dr. Ram Kumar"
                    data-testid="doctor-name-input"
                  />
                </div>
                <div>
                  <Label>{t('status')}</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ongoing">{t('ongoing')}</SelectItem>
                      <SelectItem value="resolved">{t('resolved')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    setEditingEntry(null);
                    setFormData({ condition: '', diagnosed_date: '', description: '', doctor_name: '', status: 'ongoing' });
                  }} className="flex-1">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#0D2340]" data-testid="save-medical-history-btn">
                    {editingEntry 
                      ? (language === 'hi' ? 'अपडेट करें' : 'Update')
                      : t('save')
                    }
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Intake Questionnaire Card */}
        <Card className={`border-2 ${intake ? 'border-green-500 bg-green-50' : 'border-[#FFB300] bg-[#FFB300]/10'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {intake ? (
                  <CheckCircle className="h-8 w-8 text-green-600" />
                ) : (
                  <ClipboardList className="h-8 w-8 text-[#FFB300]" />
                )}
                <div>
                  <h3 className="font-semibold font-['Manrope']">
                    {intake 
                      ? (language === 'hi' ? 'स्वास्थ्य प्रश्नावली पूर्ण' : 'Health Questionnaire Complete')
                      : (language === 'hi' ? 'स्वास्थ्य प्रश्नावली भरें' : 'Complete Health Questionnaire')
                    }
                  </h3>
                  <p className="text-sm text-slate-600">
                    {intake 
                      ? (language === 'hi' ? 'आपकी जानकारी सहेजी गई है' : 'Your information has been saved')
                      : (language === 'hi' ? 'पारिवारिक इतिहास, एलर्जी, जीवनशैली आदि' : 'Family history, allergies, lifestyle & more')
                    }
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/medical-history/intake')}
                className={intake ? 'bg-green-600' : 'bg-[#FFB300] text-slate-900'}
                data-testid="intake-questionnaire-btn"
              >
                {intake 
                  ? (language === 'hi' ? 'देखें / अपडेट करें' : 'View / Update')
                  : (language === 'hi' ? 'शुरू करें' : 'Start Now')
                }
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-10">{t('loading')}</div>
        ) : entries.length === 0 ? (
          <Card className="text-center py-10">
            <CardContent>
              <Heart className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t('noData')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className="card-interactive" data-testid={`medical-history-card-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg font-['Manrope']">{entry.condition}</h3>
                        <Badge variant={entry.status === 'ongoing' ? 'destructive' : 'secondary'}>
                          {entry.status === 'ongoing' ? t('ongoing') : t('resolved')}
                        </Badge>
                      </div>
                      <p className="text-slate-600 text-sm mb-2">{entry.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <span>{t('diagnosedDate')}: {entry.diagnosed_date}</span>
                        {entry.doctor_name && <span>{t('doctorName')}: {entry.doctor_name}</span>}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <Badge className="blockchain-badge text-xs">
                          <ShieldCheck className="h-3 w-3" />
                          {entry.blockchain_hash?.slice(0, 12)}...
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(entry)}
                        className="text-blue-500 hover:text-blue-700"
                        data-testid={`edit-${entry.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => speak(`${entry.condition}. ${entry.description}`)}
                        data-testid={`speak-${entry.id}`}
                      >
                        <Volume2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(entry.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {language === 'hi' ? 'क्या आप वाकई हटाना चाहते हैं?' : 'Delete this record?'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-500 text-sm">
            {language === 'hi' ? 'यह क्रिया पूर्ववत नहीं की जा सकती।' : 'This action cannot be undone.'}
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1">
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button onClick={confirmDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white">
              {language === 'hi' ? 'हटाएं' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MedicalHistoryPage;
